import { z } from "zod";
import type { Operation } from "../types";

/**
 * `GET /api/shell` — the chrome's data seam (RO-11-01 / #44586).
 *
 * One flat request replaces the ~12 synchronous better-sqlite3 reads the Next
 * root layout performed before paint. The SPA seeds `ShellData` from this
 * response at boot (`client/main.tsx` → `setShellData(await apiCall(shell.get))`).
 *
 * Additive TopNav fields (`projects` / `allProjects` / `groups` /
 * `activeWorkspaceName` / `defaultSlug`) come from `loadRailScope()` so the
 * project switcher has data without a second round trip.
 */

const shellSection = z.object({
  slug: z.string(),
  name: z.string(),
});

const scopeProject = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  group_id: z.number().nullable(),
  parent_id: z.number().nullable(),
  role: z.string().nullable(),
});

const folder = z.object({
  id: z.number(),
  name: z.string(),
});

const shellQuery = z
  .object({
    /** Optional project slug so rail sections resolve under project scope. */
    projectSlug: z.string().min(1).optional(),
  })
  .optional();

const shellResponse = z.object({
  onboardingActive: z.boolean(),
  needsWorkspace: z.boolean(),
  workspaceId: z.number().nullable(),
  theme: z.object({
    dbTheme: z.string(),
    accent: z.string(),
  }),
  // z.any() (not z.unknown()) so the field stays required in the inferred type —
  // z.unknown().nullable() widens to optional under some TS/zod versions and
  // breaks assignability to ShellData.lexicon.
  lexicon: z.any().nullable(),
  reviewCount: z.number(),
  pulse: z.object({
    enabled: z.boolean(),
    dynamic: z.boolean(),
  }),
  copilot: z.object({
    enabled: z.boolean(),
    develop: z.boolean(),
  }),
  rail: z.object({
    sections: z.array(shellSection),
    totalSections: z.number(),
  }),
  ptyPort: z.number(),
  // Additive TopNav / loadRailScope fields
  projects: z.array(scopeProject),
  allProjects: z.array(z.object({ slug: z.string(), name: z.string() })),
  groups: z.array(folder),
  activeWorkspaceName: z.string(),
  defaultSlug: z.string().nullable(),
  /** Workspaces the signed-in user can switch into (org dropdown). */
  workspaces: z.array(
    z.object({
      id: z.number(),
      slug: z.string(),
      name: z.string(),
    }),
  ),
});

export type ShellResponse = z.infer<typeof shellResponse>;
export type ShellQuery = z.infer<typeof shellQuery>;

const getOp: Operation<void, ShellQuery, void, ShellResponse> = {
  method: "GET",
  path: () => "/api/shell",
  query: shellQuery as z.ZodType<ShellQuery>,
  response: shellResponse,
};

export const shell = {
  get: getOp,
};
